import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import LogoPlaceholder from './LogoPlaceholder';
import * as FiIcons from 'react-icons/fi';

const { FiMapPin, FiLinkedin, FiTwitter, FiArrowUp } = FiIcons;

const Footer = () => {
  const [logoError, setLogoError] = React.useState(false);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLogoError = () => {
    setLogoError(true);
  };

  const quickLinks = [
    { name: 'Home', id: 'hero' },
    { name: 'About', id: 'about' },
    { name: 'Services', id: 'services' },
    { name: 'Contact', id: 'contact' }
  ];

  const services = [
    { name: 'Appointment Booking', link: '/services/appointment-booking' },
    { name: 'AI Chatbots', link: '/services/ai-chatbots' },
    { name: 'CRM Automation', link: '/services/crm-automation' },
    { name: 'Document Generation', link: '/services/document-generation' },
    { name: 'Website Designing', link: '/services/website-designing' },
    { name: 'Microsoft Automation', link: '/services/microsoft-automation' }
  ];

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-10 md:py-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="lg:col-span-2"
          >
            <div className="mb-4 md:mb-6 flex items-center space-x-3">
              {logoError ? (
                <LogoPlaceholder className="h-8 md:h-10 w-auto" />
              ) : (
                <img
                  src="/logo.png"
                  alt="Business Process Automation Solutions Logo"
                  className="h-8 md:h-10 w-auto"
                  onError={handleLogoError}
                  onLoad={() => setLogoError(false)}
                />
              )}
            </div>
            <p className="text-sm md:text-base text-gray-400 mb-4 md:mb-6 leading-relaxed max-w-md">
              Transforming businesses through intelligent automation solutions. We help you achieve more in less time with cutting-edge technology and expert implementation.
            </p>
            <div className="space-y-2 md:space-y-3">
              <div className="flex items-center space-x-2 md:space-x-3">
                <SafeIcon icon={FiMapPin} className="h-4 w-4 md:h-5 md:w-5 text-primary-400" />
                <span className="text-xs md:text-sm text-gray-400">81-83 Campbell St, Surry Hills, NSW 2010</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <h3 className="text-base md:text-xl font-bold mb-4 md:mb-6">Quick Links</h3>
            <ul className="space-y-2 md:space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-xs md:text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h3 className="text-base md:text-xl font-bold mb-4 md:mb-6">Our Services</h3>
            <ul className="space-y-2 md:space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <Link
                    to={service.link}
                    className="text-xs md:text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="py-8 md:py-12 border-t border-gray-800"
        >
          <div className="text-center">
            <h3 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">Ready to Transform Your Business?</h3>
            <p className="text-sm md:text-base text-gray-400 mb-6 md:mb-8 max-w-2xl mx-auto">
              Schedule a free discovery call to discuss how our automation solutions can streamline your operations and boost productivity.
            </p>
            <a
              href="https://tidycal.com/anishmmalhotra/initial-20-minutes-discussion"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 bg-primary-600 text-white px-6 py-3 md:px-8 md:py-4 rounded-lg hover:bg-primary-700 transition-all duration-300 font-semibold text-sm md:text-base shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              <span>Book Your Free Discovery Call</span>
              <SafeIcon icon={FiArrowUp} className="h-4 w-4 md:h-5 md:w-5 rotate-45" />
            </a>
          </div>
        </motion.div>

        <div className="py-6 md:py-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-xs md:text-sm text-gray-400">
              © 2024 Business Process Automation Solutions. All rights reserved.
            </div>
            <div className="flex items-center space-x-4">
              <a
                href="#"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="LinkedIn"
              >
                <SafeIcon icon={FiLinkedin} className="h-4 w-4 md:h-5 md:w-5" />
              </a>
              <a
                href="#"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Twitter"
              >
                <SafeIcon icon={FiTwitter} className="h-4 w-4 md:h-5 md:w-5" />
              </a>
            </div>
            <button
              onClick={scrollToTop}
              className="text-gray-400 hover:text-white transition-colors flex items-center space-x-2"
            >
              <span className="text-xs md:text-sm">Back to Top</span>
              <SafeIcon icon={FiArrowUp} className="h-3 w-3 md:h-4 md:w-4" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;